/**
 * Thin customer fetch for published tenant_store_policies.
 * List: GET /customer/store-policies — 404/unpublished → null strip.
 * Document: GET /customer/store-policies/documents/:kind via ApiClient.requestText (tenant headers).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, type ApiClient } from '../../shared/api/ApiClient.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { isReservedCustomerPathSegment } from '../auth/customerAuthPaths.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import type {
  TenantStorePoliciesDTO,
  TenantStorePolicyKind,
  TenantStorePolicyLinkDTO,
} from './storePoliciesTypes.js';

const POLICY_KINDS = new Set<TenantStorePolicyKind>([
  'TERMS',
  'PRIVACY',
  'RETURNS_AND_COMPLAINTS',
  'WITHDRAWAL_RIGHTS',
]);

/** App-route href for MoR strip (not bare /api/v1 — FE fetches with tenant headers). */
export function buildStorePolicyAppHref(
  tenantCode: string,
  kind: TenantStorePolicyKind,
): string {
  return `/${encodeURIComponent(tenantCode.trim())}/policies/${kind}`;
}

function normalizePolicyLink(
  raw: unknown,
  tenantCode: string,
): TenantStorePolicyLinkDTO | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const kind = typeof row.kind === 'string' ? row.kind.trim().toUpperCase() : '';
  if (!POLICY_KINDS.has(kind as TenantStorePolicyKind)) {
    return null;
  }
  const kindTyped = kind as TenantStorePolicyKind;
  const hrefRaw = typeof row.href === 'string' ? row.href.trim() : '';
  // Prefer kind-driven app route; rewrite any legacy bare API path away from target=_blank.
  const href =
    hrefRaw.length > 0 && !hrefRaw.includes('/api/')
      ? hrefRaw
      : buildStorePolicyAppHref(tenantCode, kindTyped);
  const label =
    typeof row.label === 'string' && row.label.trim().length > 0 ? row.label.trim() : null;
  return {
    kind: kindTyped,
    href,
    label,
  };
}

function normalizeStorePolicies(
  raw: unknown,
  tenantCode: string,
): TenantStorePoliciesDTO | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const body = raw as Record<string, unknown>;
  const published = body.published === true;
  const list = Array.isArray(body.policies) ? body.policies : [];
  const policies = list
    .map((row) => normalizePolicyLink(row, tenantCode))
    .filter((p): p is TenantStorePolicyLinkDTO => p != null);
  if (!published && policies.length === 0) {
    return null;
  }
  return { published, policies };
}

export type StorePoliciesQueryResult = UseQueryResult<TenantStorePoliciesDTO | null, Error>;

export function useTenantStorePoliciesQuery(
  apiTenantCode?: string | null,
): StorePoliciesQueryResult {
  const api = useApiClient();
  const routeTenantCode = useTenantCode();
  const tenantCode =
    typeof apiTenantCode === 'string' && apiTenantCode.trim().length > 0
      ? apiTenantCode.trim()
      : routeTenantCode;

  return useQuery({
    queryKey: customerQueryKeys.storePolicies(tenantCode),
    queryFn: async (): Promise<TenantStorePoliciesDTO | null> => {
      try {
        const raw = await api.request<unknown>('/customer/store-policies', {
          tenantCode,
        });
        return normalizeStorePolicies(raw, tenantCode);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return null;
        }
        throw err;
      }
    },
    enabled:
      !isPlatformChromeTenant(tenantCode) &&
      !isReservedCustomerPathSegment(tenantCode) &&
      hasCustomerApiTenant(tenantCode),
    staleTime: 60_000,
    retry: 1,
  });
}

/** Fetch published store-policy HTML with tenant-scoped ApiClient (headers + path). */
export async function fetchStorePolicyDocumentHtml(
  api: ApiClient,
  tenantCode: string,
  kind: TenantStorePolicyKind,
): Promise<string> {
  const html = await api.requestText(`/customer/store-policies/documents/${kind}`, {
    tenantCode,
  });
  const trimmed = html.trim();
  if (trimmed.length === 0) {
    throw new ApiError('Store policy document is empty', 404, 'NOT_FOUND');
  }
  return trimmed;
}
